import { useState } from 'react';
import { css } from '@emotion/react';
import { noop } from 'lodash-es';
import { FaxQueries } from '@frontend/api-fax';
import { FaxDraftQueries } from '@frontend/api-fax-draft';
import { FaxMediaQueries } from '@frontend/api-fax-media';
import { NoAccessEmptyState, StartNewFaxSVG, UploadCoverSheetSVG } from '@frontend/assets';
import { EmptyStates } from '@frontend/components';
import { StartNewFax, UploadCoversheet } from '@frontend/fax-assets#svg';
import { HttpError } from '@frontend/fetch';
import { useTranslation } from '@frontend/i18n';
import { Page } from '@frontend/page';
import { breakpoints, useMatchMedia } from '@frontend/responsiveness';
import { useAppScopeStore, useScopedAppFlagStore } from '@frontend/scope';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { FaxPrefixes } from '@frontend/tracking-prefixes';
import { useNavigate, useSearch } from '@frontend/weave-router';
import { theme } from '@frontend/theme';
import {
  SpinningLoader,
  Text,
  TextLink,
  useFileUpload,
  useAlert,
  Button,
  NakedButton,
  SearchField,
  useFormField,
  useDebouncedValue,
} from '@frontend/design-system';
import { LeaveConfirmationModal } from '../components/draft/LeaveConfirmationModal';
import { DraftCreationCard } from '../components/draft-coversheet/draft-creation-card';
import { TemplateList } from '../components/templates/template-list';
import { defaultHTML } from '../constants';

export const FaxDraftChooseCoversheetPage = ({ draftId }: { draftId: string }) => {
  const { t } = useTranslation('fax');
  const navigate = useNavigate();
  const { navigate: settingsNavigate } = useSettingsNavigate();
  const alert = useAlert();
  const isMobile = useMatchMedia({ maxWidth: breakpoints.mobile.max });
  const isXSmall = useMatchMedia({ maxWidth: breakpoints.xsmall.max });
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const faxTemplatesFlag = getFeatureFlagValue('fax-templates');
  const search = useSearch({ from: '/fax/drafts/$draftId/begin', shouldThrow: false });
  const { selectedLocationId, contactId } = search ?? {};
  const { selectedLocationIds } = useAppScopeStore();
  const locationId = selectedLocationId || selectedLocationIds[0];
  const [isUploading, setIsUploading] = useState(false);
  const [isCreateFaxLoading, setIsCreateFaxLoading] = useState(false);
  const {
    data: faxNumber,
    isFetched: isGetFaxNumberFetched,
    isLoading: isGetFaxNumberLoading,
    isError: isGetFaxNumberError,
  } = FaxQueries.useQueryDefaultFaxNumber(locationId);

  const searchFieldProps = useFormField({
    type: 'text',
    value: '',
  });

  const debouncedSearch = useDebouncedValue(searchFieldProps.value.trim());

  const invalidate = FaxDraftQueries.useInvalidateDraft();
  // const { mutateAsync: renderCoversheet } = FaxDraftQueries.useRenderCoversheet({});
  const { mutate: setCoversheetUploadedFile } = FaxDraftQueries.useSetCoversheetUploadedFile({
    onSuccess: () => {
      invalidate(draftId, locationId, selectedLocationIds);
      navigate({ to: '/fax/drafts/$draftId/upload', params: { draftId }, search: { selectedLocationId } });
    },
    onSettled: () => {
      setIsUploading(false);
    },
  });

  const { mutate: upload } = FaxDraftQueries.useUploadMedia({
    onSuccess: (data) => {
      if (data[0]?.mediaId) {
        setCoversheetUploadedFile({ draftId, locationId, mediaId: data[0]?.mediaId });
      } else {
        setIsUploading(true);
      }
      resetFiles();
    },
    onError: (err) => {
      alert.error(err instanceof HttpError ? err.message : t('An unknown error occurred'));
      resetFiles();
      setIsUploading(false);
    },
  });

  const {
    data,
    isLoading: isGetTemplatesLoading,
    isFetching: isGetTemplatesFetching,
    isError: isGetTemplatesError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = FaxMediaQueries.useInfiniteListCoversheetTemplate(locationId, [locationId], debouncedSearch, {
    enabled: faxTemplatesFlag,
  });

  const templates = data ? data.pages.flatMap((page) => page.coversheetTemplates) : [];

  const {
    // validFiles,
    // isDragging,
    // error: uploadError,
    inputProps,
    // dropContainerProps,
    handleOnClick: openFileDialog,
    resetFiles,
  } = useFileUpload({
    acceptedFileType: ['image'],
    onFileUpload: (files) => {
      setIsUploading(true);
      upload([[files[0]], locationId]);
    },
  });

  const { data: draft } = FaxDraftQueries.useGetDraft(locationId, draftId, {
    enabled: !!draftId,
    keepPreviousData: true,
    refetchOnMount: true,
  });

  const { mutateAsync: setHTML } = FaxDraftQueries.useUpdateCoversheetHtml({});
  const { mutateAsync: deleteUploadedCoversheet, isLoading: isDeletingUploadedCoversheet } =
    FaxDraftQueries.useUnsetCoversheetUploadedFile({});
  const { mutateAsync: deleteHtmlCoversheet, isLoading: isDeletingHtmlCoversheet } =
    FaxDraftQueries.useUnsetCoversheetHtml({});

  const showMissingPhoneMessage = () => {
    alert.error(
      t('Your location does not have a fax number to send from. Please reach out to support to set this up.')
    );
  };

  const deleteCoversheet = async () => {
    //note: we want both of these if statements to execute if the conditions are met. Don't return inside them
    if (draft?.coversheet?.html) {
      await deleteHtmlCoversheet({ draftId, locationId }).catch(noop);
    }
    if (draft?.uploadedCoversheet?.smallThumbnailId) {
      await deleteUploadedCoversheet({ draftId, locationId }).catch(noop);
    }
    invalidate(draftId, locationId, selectedLocationIds);
    return Promise.resolve();
  };

  const createNewFaxFromScratch = async () => {
    if (!draft) {
      return;
    }
    if (!faxNumber) {
      return showMissingPhoneMessage();
    }

    setIsCreateFaxLoading(true);
    if (draft.uploadedCoversheet) {
      await deleteUploadedCoversheet({ draftId, locationId }).catch(() => {
        alert.error(t('Unable to delete uploaded coversheet!'));
      });
      invalidate(draftId, locationId, selectedLocationIds);
    }

    if (draft.coversheet?.html) {
      await deleteHtmlCoversheet({ draftId, locationId }).catch(() => {
        alert.error(t('Unable to delete existing coversheet!'));
      });
      invalidate(draftId, locationId, selectedLocationIds);
    }

    return setHTML({
      draftId,
      coversheetHtml: `<html><body>${defaultHTML}</body></html>`,
      locationId,
    })
      .then(() => {
        invalidate(draftId, locationId, selectedLocationIds);
        navigate({ to: '/fax/drafts/$draftId/cover', params: { draftId }, search: { selectedLocationId } });
      })
      .finally(() => {
        setIsCreateFaxLoading(false);
      });
  };

  const createNewFaxWithoutCoverSheet = () => {
    if (!faxNumber) {
      return showMissingPhoneMessage();
    }

    deleteCoversheet().finally(() => {
      invalidate(draftId, locationId, selectedLocationIds);
      return navigate({
        to: '/fax/drafts/$draftId/upload',
        params: { draftId },
        search: { selectedLocationId },
      });
    });
  };

  const createNewFaxFromUpload = () => {
    openFileDialog();
  };

  const getNextPage = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  const isLoading = isDeletingHtmlCoversheet || isDeletingUploadedCoversheet || isUploading;
  return (
    <Page
      css={{
        '.page-header': {
          marginBottom: theme.spacing(3),
          backgroundColor: theme.colors.white,
        },
      }}
    >
      <Page.Header>
        <Page.Header.Breadcrumbs
          breadcrumbs={[
            { label: t('Fax Inbox'), to: '/fax/inbox' },
            { label: faxTemplatesFlag ? t('Select Cover Sheet') : t('Select a Cover Sheet Type') },
          ]}
        />
        <Page.Header.Heading>
          <Page.Header.Title title={faxTemplatesFlag ? t('Select Cover Sheet') : t('Select a Cover Sheet Type')} />
          <Page.Header.Subtitle
            subtitle={
              !faxTemplatesFlag && !!faxNumber
                ? t(
                    'Every good fax has a cover sheet. Select which kind of cover sheet you would like to proceed with to build your new fax message.'
                  )
                : null
            }
          />
          <Page.Header.Action>
            {faxNumber ? (
              <Button
                variant='secondary'
                onClick={createNewFaxWithoutCoverSheet}
                trackingId={`${FaxPrefixes.New}-skip-cover-sheet`}
              >
                {t('Skip Cover Sheet')}
              </Button>
            ) : null}
          </Page.Header.Action>
        </Page.Header.Heading>
      </Page.Header>
      <Page.Body css={{ display: 'flex', flexDirection: 'column' }}>
        {isGetFaxNumberLoading ? (
          <SpinningLoader
            css={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
            }}
          />
        ) : isGetFaxNumberError ? (
          <EmptyStates
            emptyStateConfig={{
              type: 'fax',
              header: t('Something went wrong. Please try again.'),
            }}
          />
        ) : faxNumber && isGetFaxNumberFetched ? (
          faxTemplatesFlag ? (
            <>
              <div
                css={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  flexDirection: isMobile && !isXSmall ? 'column-reverse' : 'row',
                  rowGap: isMobile && !isXSmall ? theme.spacing(2) : 0,
                  columnGap: theme.spacing(2),
                  marginBottom: theme.spacing(3),
                  paddingBottom: theme.spacing(3),
                  borderBottom: `1px solid ${theme.colors.neutral20}`,
                }}
              >
                <NakedButton
                  css={{
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    columnGap: theme.spacing(2),
                    border: `1px solid ${theme.colors.neutral20}`,
                    borderRadius: theme.borderRadius.medium,
                    padding: theme.spacing(1),
                    flex: '1 1 0',
                    maxWidth: 350,
                    minWidth: 0,
                    '&:hover': {
                      border: `1px solid ${theme.colors.primary50}`,
                    },
                  }}
                  onClick={createNewFaxFromScratch}
                  disabled={isCreateFaxLoading}
                >
                  <StartNewFax css={{ maxWidth: 70 }} />
                  <div
                    css={{
                      display: 'flex',
                      justifyContent: 'flex-start',
                      alignItems: 'flex-start',
                      flexDirection: 'column',
                    }}
                  >
                    <Text weight='bold' size='large'>
                      {isCreateFaxLoading ? t('Creating New Fax...') : isXSmall ? t('New') : t('Start New')}
                    </Text>
                    {!isXSmall &&
                      (isCreateFaxLoading ? (
                        <SpinningLoader css={{ margin: 'auto' }} size='medium' />
                      ) : (
                        <Text color='subdued' size='small'>
                          {t('Create a new fax cover sheet')}
                        </Text>
                      ))}
                  </div>
                </NakedButton>
                <NakedButton
                  css={{
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    columnGap: theme.spacing(2),
                    border: `1px solid ${theme.colors.neutral20}`,
                    borderRadius: theme.borderRadius.medium,
                    padding: theme.spacing(1),
                    flex: '1 1 0',
                    maxWidth: 350,
                    minWidth: 0,
                    '&:hover': {
                      border: `1px solid ${theme.colors.primary50}`,
                    },
                  }}
                  onClick={() => createNewFaxFromUpload()}
                  disabled={isUploading}
                >
                  <UploadCoversheet css={{ maxWidth: 70 }} />
                  <div
                    css={{
                      display: 'flex',
                      justifyContent: 'flex-start',
                      alignItems: 'flex-start',
                      flexDirection: 'column',
                    }}
                  >
                    <Text weight='bold' size='large' textAlign='left'>
                      {isUploading ? t('Uploading Cover Sheet...') : isXSmall ? t('Upload') : t('Upload Cover Sheet')}
                    </Text>
                    {!isXSmall &&
                      (isUploading ? (
                        <SpinningLoader css={{ margin: 'auto' }} size='medium' />
                      ) : (
                        <Text color='subdued' size='small' textAlign='left'>
                          {t('Use another document as your fax cover')}
                        </Text>
                      ))}
                  </div>
                </NakedButton>
              </div>
              <div
                css={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  marginBottom: theme.spacing(2),
                  columnGap: theme.spacing(1),
                }}
              >
                <SearchField {...searchFieldProps} name='templates-search' />
                <Button
                  iconName='plus'
                  variant='secondary'
                  size='large'
                  onClick={() => {
                    navigate({
                      to: '/fax/drafts/template-editor',
                      search: {
                        selectedLocationId: locationId,
                      },
                    });
                  }}
                  data-trackingid={`create-fax-template`}
                >
                  {t('Create Template')}
                </Button>
              </div>
              <TemplateList
                locationId={locationId}
                isError={isGetTemplatesError}
                templates={templates}
                isLoading={isGetTemplatesLoading}
                isFetching={isGetTemplatesFetching || isFetchingNextPage}
                contactId={contactId}
                disableTemplates={isUploading || isCreateFaxLoading}
                draftId={draftId}
                trackingId={`use-fax-template`}
                getNextPage={getNextPage}
              />
            </>
          ) : (
            <ul
              css={css`
                display: flex;
                margin: 0;
                padding: 0;
              `}
            >
              <DraftCreationCard
                title={t('Start New')}
                description={t('Create a brand new cover sheet with the fax builder.')}
                buttonLabel={t('Start Cover Sheet')}
                color={theme.colors.success5}
                onSelect={() => createNewFaxFromScratch()}
                buttonDisabled={isLoading}
                imageSrc={StartNewFaxSVG}
              />
              <DraftCreationCard
                title={t('Upload Cover Sheet')}
                description={
                  isUploading ? (
                    <SpinningLoader />
                  ) : (
                    t('Want to upload your own cover sheet? Jump right into uploading your fax documents.')
                  )
                }
                buttonLabel={isUploading ? t('Uploading...') : t('Upload My Own')}
                buttonDisabled={isUploading}
                color={theme.colors.primary10}
                onSelect={isUploading ? () => {} : () => createNewFaxFromUpload()}
                imageSrc={UploadCoverSheetSVG}
              />
            </ul>
          )
        ) : (
          <NoAccessEmptyState
            message={
              <>
                {t(
                  'No fax numbers are available for this location — none may be set up, or all are privately assigned to other users. Manage access in '
                )}{' '}
                <TextLink onClick={() => settingsNavigate({ to: '/organization/phone-numbers' })}>
                  {t('Phone Numbers and Porting')}
                </TextLink>
              </>
            }
          />
        )}
        <input type='file' id='file-drop' hidden {...inputProps} />
        <LeaveConfirmationModal draftId={draftId} shouldBlock={true} locationId={locationId} />
      </Page.Body>
    </Page>
  );
};
